import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import Button from '../../../../core/components/Button';
import Dialog from '../../../../core/components/Dialog';
import { useWacOrdering } from './useWacOrdering';

type WacDialog = 'STOP' | 'RESTART' | null;

// Page-level WAC ordering circuit breaker rendered top-right of the Data Monitor tab bar.
// Caller gates this to VERITYSA only.
const WacOrderingControl = (): JSX.Element | null => {
    const { wacOrderingStatus, waiting, stopWacOrders, restartWacOrders } = useWacOrdering();
    const [dialog, setDialog] = useState<WacDialog>(null);

    const running = wacOrderingStatus === true;

    const openDialog = useCallback((): void => {
        if (waiting) return;
        setDialog(running ? 'STOP' : 'RESTART');
    }, [waiting, running]);

    const closeDialog = useCallback((): void => setDialog(null), []);

    const confirmStop = useCallback(async (): Promise<void> => {
        setDialog(null);
        await stopWacOrders();
    }, [stopWacOrders]);

    const confirmRestart = useCallback(
        async (heldAction: 'EXPORT' | 'CANCEL'): Promise<void> => {
            setDialog(null);
            await restartWacOrders(heldAction);
        },
        [restartWacOrders],
    );

    const stopActions = useMemo(
        () => [
            { label: 'CANCEL', cssName: 'btn-cancel', onClick: closeDialog },
            { label: 'OK', onClick: (): void => void confirmStop() },
        ],
        [closeDialog, confirmStop],
    );

    const restartActions = useMemo(
        () => [
            { label: 'SEND HELD ORDERS', onClick: (): void => void confirmRestart('EXPORT') },
            { label: 'CANCEL HELD ORDERS', cssName: 'btn-danger', onClick: (): void => void confirmRestart('CANCEL') },
            { label: 'CANCEL', cssName: 'btn-cancel', onClick: closeDialog },
        ],
        [closeDialog, confirmRestart],
    );

    // status not yet known → render nothing to avoid flashing the wrong label
    if (wacOrderingStatus === null) return null;

    return (
        <div className="datamonitor-wac-control">
            <Button
                id="datamonitorWacBtn"
                cssName="btn-delete"
                onClick={openDialog}
                disabled={waiting || dialog !== null}
            >
                <FontAwesomeIcon icon={running ? faBan : faArrowsRotate} />
                <span>{running ? 'Stop All WAC Orders' : 'Restart WAC Orders'}</span>
            </Button>

            <Dialog
                open={dialog === 'STOP'}
                setOpen={closeDialog}
                title="Stop WAC Orders"
                message="Confirm stopping all outgoing WAC orders. Orders will be held until restarted."
                actions={stopActions}
            />
            <Dialog
                open={dialog === 'RESTART'}
                setOpen={closeDialog}
                title="Restart WAC Orders"
                message="Confirm restarting WAC ordering. Choose an action for held orders. This is NOT reversible."
                actions={restartActions}
            />
        </div>
    );
};

export default WacOrderingControl;
